import type { FreeReadingContent, PaidReading } from './reading-schemas';

export const FOCUS_VALUES = [
  'love_and_relationships',
  'money_and_career',
  'purpose_and_direction',
  'healing_and_inner_peace',
  'my_whole_path',
] as const;

export type Focus = (typeof FOCUS_VALUES)[number];
export type FocusSelection = Focus;
export type BirthTimeStatus = 'known' | 'unknown';
export type BirthMeridiem = 'AM' | 'PM';
export type PersonalizationGender = 'female' | 'male';
export type FunnelStep = 'landing' | 'birth_date' | 'birth_details' | 'details' | 'focus' | 'profile' | 'loading' | 'free_result' | 'paid_loading' | 'paid_result';
export type SessionStatus = 'started' | 'details_completed' | 'focus_completed' | 'generating_free' | 'free_ready' | 'checkout_started' | 'purchased' | 'generating_paid' | 'paid_ready' | 'generation_failed' | 'abandoned';
export type { CurrentFreeReading, CurrentPaidReading, ForecastMonth, HistoricalFreeV2Reading, HistoricalFreeV3Reading, LegacyFreeReading, LegacyPaidReading, PaidReading } from './reading-schemas';

export interface Attribution {
  source?: string;
  medium?: string;
  campaign?: string;
  manychat_channel?: string;
  manychat_profile?: string;
  manychat_flow?: string;
  manychat_automation?: string;
}

export interface ProfileInput { first_name: string; birth_date: string; gender_personalization: PersonalizationGender }

export interface BirthDetailsInput {
  birth_time_status: BirthTimeStatus;
  birth_hour?: number;
  birth_minute?: number;
  birth_meridiem?: BirthMeridiem;
  birthplace?: string;
}

export interface PricingOffer {
  experiment_key: string | null;
  experiment_version: string | null;
  variant: 'summer_999' | 'summer_1499' | 'price_1699' | 'price_1499' | 'price_999' | 'standard_999' | 'standard_2499';
  currency: 'usd';
  current_price_cents: number;
  reference_price_cents: number | null;
  show_reference_price: boolean;
  sale_label: string | null;
}

export type AlunarPackageId = 'alunar_3m' | 'alunar_6m' | 'alunar_12m';
export type AlunarUpgradeId = 'alunar_upgrade_3m_to_6m' | 'alunar_upgrade_3m_to_12m' | 'alunar_upgrade_6m_to_12m';
export interface PackageEntitlement { package_id: AlunarPackageId; package_months: 3 | 6 | 12 }
export interface AlunarUpgradeOffer {
  upgrade_id: AlunarUpgradeId;
  from_package_id: AlunarPackageId;
  to_package_id: AlunarPackageId;
  target_months: 6 | 12;
  price_cents: 399 | 799;
  currency: 'usd';
  label: string;
  badge?: 'BEST VALUE';
}

export interface PublicTestimonial {
  display_name: string;
  testimonial_text: string;
  rating: number | null;
  order: number | null;
  verified_reader: boolean;
}

export interface TestimonialSubmissionInput {
  reading_session_id: string;
  display_name: string;
  testimonial_text: string;
  rating?: number | null;
  consent: true;
}

export interface AppState extends Attribution {
  visitor_id: string;
  journey_id?: string;
  journey_token?: string;
  reading_session_id?: string;
  profile_id?: string;
  resume_token?: string;
  first_name: string;
  birth_date: string;
  birth_time_status?: BirthTimeStatus;
  birth_hour?: number;
  birth_minute?: number;
  birth_meridiem?: BirthMeridiem;
  birthplace?: string;
  gender_personalization?: PersonalizationGender;
  focus_selections: FocusSelection[];
  focus?: Focus;
  step: FunnelStep;
  status: SessionStatus;
  funnel_version: 'phase1' | 'phase1_v2' | 'alunar-2026-09-v1';
  prefilled: boolean;
  returning_visitor: boolean;
  free_reading?: FreeReading;
  paid_reading?: PaidReading;
  pricing_offer?: PricingOffer;
  entitlement?: PackageEntitlement;
  upgrade_offers?: AlunarUpgradeOffer[];
  selected_package_id?: AlunarPackageId;
  checkout_cancelled?: boolean;
}

export type FreeReading = FreeReadingContent & {
  astrology_data?: AstrologyData;
  fallback_used?: boolean;
};

export interface AstrologyData {
  strategy: 'utc_date_boundaries';
  sun: Placement & { decan: number; element: string; modality: string; polarity: string; ruler: string };
  moon_sign_status: 'confirmed_for_date' | 'uncertain_without_time';
  moon_sign?: string;
  moon_sign_options?: string[];
  moon_phase: string;
  planets: Record<string, Placement>;
  numerology: { life_path: number; birthday_number: number; personal_year: number; personal_months: Array<{ month: string; value: number }> };
  aspects: Array<{ bodies: string[]; aspect: string; orb: number }>;
  upcoming_transits: Array<{ date: string; body: string; event: string }>;
}

export interface Placement { sign: string; longitude: number; degree: number }
